import { Row, Col, Button } from "react-bootstrap";
import { Printer } from "react-bootstrap-icons";
import { PMGradingRow } from "./types";
import { ProductionFormStateWithFrontEndOnlyFields } from "./utilities";

type ProductionFormHeaderBarProps = {
  darkMode: boolean | null;
  lastRetrieved: Record<string, number>;
  activePMID: string;
  lastModified: Record<string, number>;
  printItemLabel: () => void;
  isLoading: boolean;
  formDataByTab: Record<
    string,
    Partial<ProductionFormStateWithFrontEndOnlyFields>
  >;
  fetchPMRow: (
    entityId: string,
    skipDatabase?: boolean
  ) => Promise<PMGradingRow | null>;
  forceFetchLatestConfiguration: () => void;
  handleSaveChanges: () => Promise<boolean>;
  handleRemoveTab: (tabId: string) => void;
  showPalletLabelPrintModal: (show: boolean) => void;
  getEffectivePMID?: (pmid: string, clientCode?: string) => string;
};

export const ProductionFormHeaderBar = (
  props: ProductionFormHeaderBarProps
) => {
  const {
    darkMode,
    lastRetrieved,
    activePMID,
    lastModified,
    printItemLabel,
    isLoading,
    formDataByTab,
    fetchPMRow,
    forceFetchLatestConfiguration,
    handleSaveChanges,
    handleRemoveTab,
    showPalletLabelPrintModal,
    getEffectivePMID,
  } = props;

  // Calculate the effective PMID to display (with updated client code)
  const displayPMID = getEffectivePMID
    ? getEffectivePMID(activePMID, formDataByTab[activePMID]?.clientCode)
    : activePMID;

  return (
    <Row
      style={{
        position: "sticky",
        top: 0,
        zIndex: 100,
        marginBottom: 10,
        padding: "20px",
        backgroundColor: darkMode ? "#343a40" : "#f2f2f2",
        borderBottom: darkMode ? "2px solid #f2f2f2" : "2px solid black",
      }}
    >
      <Col>
        <h2>{displayPMID}</h2>
        {displayPMID !== activePMID && (
          <small style={{ color: "#888" }}>(Original PMID: {activePMID})</small>
        )}
      </Col>
      <Col>
        <Row>
          <Col>
            <small style={{ color: "#888" }}>
              Last retrieved:{" "}
              {lastRetrieved[activePMID]
                ? new Date(lastRetrieved[activePMID]).toLocaleString()
                : "Never"}
            </small>
          </Col>
        </Row>
        <Row>
          <Col>
            <small style={{ color: "#888" }}>
              Last modified:{" "}
              {lastModified[activePMID]
                ? new Date(lastModified[activePMID]).toLocaleString()
                : "Never"}
            </small>
          </Col>
        </Row>
      </Col>
      <Row className="g-2">
        <Col xs={12} sm={4}>
          <Button
            variant="primary"
            onClick={printItemLabel}
            className="w-100"
            disabled={
              isLoading || !formDataByTab[activePMID]?.productionNumberOfBoxes
            }
          >
            <Printer className="me-2" size={20} />
            Print {formDataByTab[activePMID]?.productionNumberOfBoxes} Item
            Label
            {formDataByTab[activePMID]?.productionNumberOfBoxes ?? 0 > 1
              ? "s"
              : ""}
          </Button>
        </Col>
        <Col xs={12} sm={4}>
          <Button
            variant="info"
            onClick={() => {
              if (showPalletLabelPrintModal) {
                showPalletLabelPrintModal(true);
              }
            }}
            className="w-100"
            disabled={isLoading}
          >
            <Printer className="me-2" size={20} />
            Print Pallet Label
          </Button>
        </Col>
        <Col xs={12} sm={4}>
          <Button
            variant="warning"
            onClick={() => {
              fetchPMRow(activePMID, true);
              forceFetchLatestConfiguration();
            }}
            disabled={isLoading}
            className="w-100"
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Loading...
              </>
            ) : (
              "Retrieve Latest from PM"
            )}
          </Button>
        </Col>
      </Row>
      <Row className="g-2">
        <Col xs={12} sm={6}>
          <Button
            onClick={handleSaveChanges}
            disabled={isLoading}
            variant="success"
            className="w-100"
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Col>
        <Col xs={12} sm={6}>
          <Button
            disabled={isLoading}
            onClick={() => {
              if (window.confirm("Close without saving any changes?")) {
                handleRemoveTab(activePMID);
              }
            }}
            variant="danger"
            className="w-100"
          >
            Close
          </Button>
        </Col>
      </Row>
    </Row>
  );
};
